import { type MouseEvent, useState } from 'react';
import { Link, Location } from 'react-router-dom';
import { default as Tag } from 'antd/es/tag';

import { type IPage } from '../models/pages';
import { useLocation } from 'react-router-dom';
import { mergeClasses } from '../utils/mergeClasses';

import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { AppSection } from '../pages/Section';
import { getColor } from '../utils/getColorOfHttpMethod';
import { scrollToTop } from '../utils/scrollToTop';

export function NavBar({
  pages,
  parentSection,
  mobileCallback
}: {
  pages: Array<IPage>;
  parentSection: AppSection;
  mobileCallback?: () => void;
}) {
  const location = useLocation();

  return (
    <nav className="navbar-container">
      <ol className="flex w-full flex-col gap-4">
        {pages.map((page) => (
          <NavbarSection
            key={page.id}
            page={page}
            location={location}
            parentSection={parentSection}
            mobileCallback={mobileCallback}
          />
        ))}
      </ol>
    </nav>
  );
}

function NavbarSection({
  page,
  parentSection,
  location,
  mobileCallback
}: {
  page: IPage;
  parentSection: string;
  location: Location<any>;
  mobileCallback?: () => void;
}) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const isSelected = page.id === location.pathname.split('/').pop();

  const sectionClasses = mergeClasses(
    'navbar-section-container',
    isSelected ? 'navbar-section-selected' : ''
  );

  return (
    <div className={sectionClasses}>
      <div className="navbar-toggle-container">
        <Link
          to={{
            pathname: `/${parentSection}/${page.id}`,
            search: new URLSearchParams(location.search).toString()
          }}
          className="navbar-link"
          onClick={(e) => {
            if (mobileCallback) {
              e.stopPropagation();
              mobileCallback();
            }
            scrollToTop();
          }}
        >
          {page.label}
        </Link>
        {page.children && !mobileCallback && (
          <button
            onClick={(e: MouseEvent) => {
              setIsCollapsed(!isCollapsed);
              e.stopPropagation();
            }}
          >
            {isCollapsed ? (
              <ChevronDownIcon width={20} height={20} color="black" />
            ) : (
              <ChevronUpIcon width={20} height={20} color="black" />
            )}
          </button>
        )}
      </div>

      {page.children && !isCollapsed && (
        <div className="flex w-full flex-col justify-between rounded-md p-1">
          {page.children.map((p) => (
            <NestedListItem
              key={p.id}
              level={1}
              child={p}
              path=""
              location={location}
              parentSection={parentSection}
              rootPage={page}
              mobileCallback={mobileCallback}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NestedListItem({
  level,
  child,
  path,
  location,
  parentSection,
  rootPage,
  mobileCallback
}: {
  level: number;
  child: IPage;
  path: string;
  location: Location<any>;
  parentSection: string;
  rootPage: IPage;
  mobileCallback?: () => void;
}) {
  const isHashHighlighted = `#${child.id}` === location.hash;
  const sectionPath = `/${parentSection}/${rootPage.id}`;

  const highlightClasses = isHashHighlighted
    ? 'navbar-highlight-classes'
    : 'text:white lg:text-inpay-black-primary-1000 dark:text-white';
  const marginClasses = level === 1 ? 'depth-1' : 'depth-2';

  const contentClasses = mergeClasses(
    'navbar-common-classes',
    'lg:hover:bg-inpay-gray-primary-400 lg:dark:hover:bg-inpay-black-primary-800',
    highlightClasses,
    marginClasses
  );

  return (
    <li className="navbar-common-classes">
      <Link
        to={{
          hash: child.id,
          pathname: sectionPath,
          search: location.search
        }}
        onClick={(e: any) => {
          mobileCallback?.();
          e.stopPropagation();
        }}
      >
        <div className={contentClasses}>
          <div className="flex flex-row items-center gap-4">
            {child.article.endpoint ? (
              <Tag
                bordered={false}
                className={mergeClasses(
                  getColor(child.article.endpoint.method),
                  'min-w-12'
                )}
              >
                <p className="text-bold text-center text-white dark:text-white">
                  {child.article.endpoint.method}
                </p>
              </Tag>
            ) : null}
            <p
              className={
                level === 2 ? 'text-white lg:text-inpay-black-primary-800' : ''
              }
            >
              {child.label}
            </p>
          </div>
        </div>
      </Link>
      {child.children && (
        <ol>
          {child.children.map((child) => (
            <NestedListItem
              key={child.id}
              level={level + 1}
              child={child}
              path={path}
              location={location}
              parentSection={parentSection}
              rootPage={rootPage}
              mobileCallback={mobileCallback}
            />
          ))}
        </ol>
      )}
    </li>
  );
}
